import { useMutation } from "@apollo/react-hooks";
import { CardElement, useElements, useStripe} from "@stripe/react-stripe-js";

import { useAppState } from "core/contexts/AppContext";
import { PAYMENT_INTENT_STATUS_SUCCEEDED, REQUIRES_ACTIONS } from "core/includes/constants";
import errorParser from "core/includes/errorParser";
import { getCurrencyByCountry } from "core/includes/finance";
import {
    ADD_USER_DETAILS_TO_ORDER, COMPLETE_ORDER,
    CREATE_STRIPE_PAYMENT_INTENT, SEND_ORDER_INVOICE,
} from "core/includes/queries";
import { FieldList } from "core/hooks/useForm";
import useSetState from "core/hooks/useSetState";
import useCart from "pages/Components/CartPage/hooks/useCart";
import { countryToPhoneCode, getCountryCodeFromName } from "theme/utils";

type Props = {
    fields: FieldList,
    detailFields: FieldList,
    shippingOption?: any,
}

type DefaultState = {
    isSubmitting: boolean,
    errorMessage: string | null,
    isVisibleErrorMessage: boolean,
    errorType: string|null,
}

const INITIAL_STATE = {
    isSubmitting: false,
    errorMessage: null,
    isVisibleErrorMessage: false,
    errorType: null,
}

const usePlaceOrder = ({ fields, detailFields, shippingOption = {} }: Props) => {
    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);
    const [appState] = useAppState();
    const stripe: any = useStripe();
    const elements: any = useElements();
    const { clearCartStorage, getCountry } = useCart();

    const [createStripePaymentIntent] = useMutation(CREATE_STRIPE_PAYMENT_INTENT);
    const [addUserDetailsToOrder] = useMutation(ADD_USER_DETAILS_TO_ORDER);
    const [completeOrder] = useMutation(COMPLETE_ORDER);
    const [sendOrderInvoice] = useMutation(SEND_ORDER_INVOICE);

    const countryCode = getCountry();

    const updateOrderDetails = async () => {
        const input = {
            "billingAddress": {
                "firstName": fields.firstName || fields.firstNameBilling,
                "lastName": fields.lastName || fields.lastNameBilling,
                "address1": fields.addressOne || fields.addressOneBilling,
                "city": fields.city || fields.cityBilling,
                "countryCode": getCountryCodeFromName(fields.country || fields.countryBilling),
                "province": fields.state || fields.stateBilling,
                "zip": fields.postCode || fields.postCodeBilling
            },
            "email": detailFields.email,
            "shippingAddress": {
                "firstName": detailFields.firstName,
                "lastName": detailFields.lastName,
                "company": detailFields.company,
                "address1": detailFields.addressOne,
                "address2": detailFields.addressTwo,
                "city": detailFields.city,
                "countryCode": getCountryCodeFromName(fields.country || fields.countryBilling),
                "phone": countryToPhoneCode(detailFields.phoneCode) + detailFields.phone,
                "province": detailFields.state,
                "zip": detailFields.postCode
            },
        };

        return addUserDetailsToOrder({
            variables: {
                orderUuid: appState.cart?.orderUuid,
                input: JSON.stringify(input),
            }
        });
    }

    const onCompleteDraftOrder = async () => {
        return completeOrder({
            variables: {
                orderUuid: appState.cart.orderUuid,
            }
        }).then((response) => {
            if (response.data.completeOrder) {
                clearCartStorage();
            }
            return response;
        }).catch((error) => {
            setState({
                errorMessage: errorParser(error),
                isVisibleErrorMessage: true,
                errorType: 'complete-order',
                isSubmitting: false,
            });

            return error;
        });
    }

    const confirmCardPaymentIfRequired = async (clientSecret: string) => {
        if (!clientSecret) return;

        const stripeResponse = await stripe.confirmCardPayment(clientSecret);

        // Handle result.error or result.paymentIntent
        if (stripeResponse.paymentIntent && stripeResponse.paymentIntent.status === PAYMENT_INTENT_STATUS_SUCCEEDED) {
            const completeDraftOrderResponse = await onCompleteDraftOrder();

            return completeDraftOrderResponse;
        } else {
            // https://stripe.com/docs/api/errors
            setState({
                errorMessage: stripeResponse?.error?.message ?? "An error occurred while processing your card. Please try again.",
                errorType: 'stripe',
                isVisibleErrorMessage: true,
                isSubmitting: false
            });
        }
    };

    const paymentSubmit = async () => {
        if (!stripe || !elements) {
            return;
        }

        setState({
            errorMessage: '',
            errorType: '',
            isVisibleErrorMessage: false,
            isSubmitting: true,
        });

        const cardElement: any = elements.getElement(CardElement);

        const { error: cardTokenError } = await stripe.createToken(cardElement);

        if (cardTokenError) {
            setState({
                errorMessage: cardTokenError.message,
                errorType: 'card-error',
                isVisibleErrorMessage: true,
                isSubmitting: false
            });

            return 'card-error';
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: fields.firstName + ' ' + fields.lastName,
                email: detailFields.email,
                phone: countryToPhoneCode(detailFields.phoneCode) + detailFields.phone,
                address: {
                    city: fields.city,
                    country: getCountryCodeFromName(fields.country),
                    line1: fields.addressOne,
                    line2: fields.addressTwo || '',
                    state: fields.state,
                },
            },
        });

        if (error) {
            setState({
                errorMessage: error.message,
                errorType: 'payment-method',
                isVisibleErrorMessage: error.errorMessage !== "Invalid email address.",
                isSubmitting: false
            });
            return;
        }

        const stripeInput = {
            email: detailFields.email,
            paymentMethodId: paymentMethod.id,
            amount: appState.cart.totalAmount,
            currency: getCurrencyByCountry(countryCode)
        }

        const paymentResponse = await createStripePaymentIntent({
            variables: {
                input: JSON.stringify(stripeInput),
                countryCode: countryCode.code
            }
        });

        try {
            const data = JSON.parse(paymentResponse.data?.createStripePaymentIntent);
            const updateOrderDetailsResponse = await updateOrderDetails();

            if (!updateOrderDetailsResponse.data.addUserDetailsToOrder) return;

            const completeOrderResponse = REQUIRES_ACTIONS.includes(data?.status) ? await confirmCardPaymentIfRequired(
                data.client_secret,
            ) : await onCompleteDraftOrder();

            return [paymentResponse, updateOrderDetailsResponse, completeOrderResponse];
        } catch (e) {
            return e;
        }
    }

    const sendInvoice = async () => {
        setState({ isSubmitting: true });

        const updateOrderDetailsResponse = await updateOrderDetails();

        if (updateOrderDetailsResponse.data.addUserDetailsToOrder) {
            const sendOrderInvoiceResponse = await sendOrderInvoice({
                variables: {
                    orderUuid: appState.cart.orderUuid,
                }
            }).then(response => {
                if (response.data.sendOrderInvoice) {
                    clearCartStorage();
                    setState({ isSubmitting: false });
                }

                return response;
            }).catch((error) => {
                setState({
                    errorMessage: errorParser(error),
                    errorType: 'send-invoice',
                    isVisibleErrorMessage: true,
                    isSubmitting: false
                });
            });

            return [updateOrderDetailsResponse, sendOrderInvoiceResponse];
        }

        return [updateOrderDetailsResponse, null];
    }

    return { paymentSubmit, sendInvoice, state, setState }
}

export default usePlaceOrder;